import { Fragment } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { Tooltip } from 'react-tooltip';
import ValueWithYear from '../../../../common/ValueWithYear';
import { ICON_BUTTON_ICONS } from '../../../../buttons/IconButton';
import { PUNCTUATION } from '../../../../../utils/constants/notation';
import {
  dataSourceClass,
  GROSS_PROFIT_TOOLTIP,
  resolveFinancialSource
} from '../../../../../utils/constants/dataSources';
import { formatNumber } from '../../../../../utils/utils';
import { BASIC_FIGURE_TOOLTIP } from './graphConstants';
import { useGraphContext } from './GraphContext';

export const FIGURE_LABELS = {
  revenue: 'Sales (€M)',
  employees: 'Employees'
};

// data-tooltip-content keys read by the one shared FigureTooltip of a graph instance.
export const FIGURE_TOOLTIP_KEYS = {
  BASIC: 'basic',
  GROSS_PROFIT: 'grossProfit'
};

const FIGURE_TOOLTIP_CONTENT = {
  [FIGURE_TOOLTIP_KEYS.BASIC]: <>{BASIC_FIGURE_TOOLTIP}</>,
  [FIGURE_TOOLTIP_KEYS.GROSS_PROFIT]: <>{GROSS_PROFIT_TOOLTIP}</>
};

/**
 * The single figure tooltip of a graph instance, with the same content, class and placement as
 * the search list. One instance per graph: react-tooltip binds anchors document-wide, and the mini
 * graph stays mounted under the full-screen popup. It renders on the page body with a fixed
 * position, so it shows above the details popover and is not clipped inside the full-screen popup
 * (a transformed box that hides its overflow).
 */
export const FigureTooltip = ({ id }) =>
  createPortal(
    <Tooltip
      id={id}
      multiline={true}
      variant="dark"
      place="bottom-center"
      opacity={1}
      positionStrategy="fixed"
      className="default-black-info-tooltip-classname sg-figure-tooltip"
      render={({ content }) => FIGURE_TOOLTIP_CONTENT[content] ?? null}
    />,
    document.body
  );

FigureTooltip.propTypes = {
  id: PropTypes.string.isRequired
};

const tooltipAnchor = (tooltipId, key) =>
  tooltipId ? { 'data-tooltip-id': tooltipId, 'data-tooltip-content': key } : {};

// A blank string is unknown, though +'' and +' ' are 0.
const isKnownNumber = (value) =>
  value !== null &&
  value !== undefined &&
  String(value).trim() !== '' &&
  !Number.isNaN(Number(value));

const EmptyValue = () => <span className="sg-figure-empty">{PUNCTUATION.EMPTY_VALUE}</span>;

const LoadingValue = () => (
  <span className="sg-figure-loading" aria-label="Loading">
    …
  </span>
);

const FigureValue = ({ value, year, isBasic, tooltipId }) => (
  <span
    className="sg-figure-number"
    {...(isBasic ? tooltipAnchor(tooltipId, FIGURE_TOOLTIP_KEYS.BASIC) : {})}
  >
    <ValueWithYear
      value={value}
      year={year}
      formatValue={false}
      className={isBasic ? 'is-basic' : ''}
    />
  </span>
);

FigureValue.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  year: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isBasic: PropTypes.bool,
  tooltipId: PropTypes.string
};

// Same presence rule as the search list: a revenue string ('0.0' included) or n/a.
const RevenueValue = ({ figures, tooltipId }) => {
  if (!figures?.revenue) return <EmptyValue />;
  return (
    <span className="sg-figure-value-wrap">
      {!!figures.isRevenueGrossProfit && (
        <span
          className="default-black-info-circle-wrap sg-gross-profit-info"
          {...tooltipAnchor(tooltipId, FIGURE_TOOLTIP_KEYS.GROSS_PROFIT)}
        >
          <img src={ICON_BUTTON_ICONS.INFO_CIRCLE} alt="" />
        </span>
      )}
      <FigureValue
        value={figures.revenue}
        year={figures.revenueYear}
        isBasic={!!figures.isRevenueBasic}
        tooltipId={tooltipId}
      />
    </span>
  );
};

RevenueValue.propTypes = {
  figures: PropTypes.object,
  tooltipId: PropTypes.string
};

const EmployeesValue = ({ figures, tooltipId }) => {
  if (!isKnownNumber(figures?.employees)) return <EmptyValue />;
  return (
    <FigureValue
      value={formatNumber((+figures.employees).toFixed(2))}
      year={figures.employeesYear}
      isBasic={!!figures.isEmployeesBasic}
      tooltipId={tooltipId}
    />
  );
};

EmployeesValue.propTypes = {
  figures: PropTypes.object,
  tooltipId: PropTypes.string
};

const figureItems = (figures) => [
  {
    key: 'revenue',
    label: FIGURE_LABELS.revenue,
    isBasic: !!figures?.isRevenueBasic,
    isKnown: !!figures?.revenue,
    Value: RevenueValue
  },
  {
    key: 'employees',
    label: FIGURE_LABELS.employees,
    isBasic: !!figures?.isEmployeesBasic,
    isKnown: isKnownNumber(figures?.employees),
    Value: EmployeesValue
  }
];

const labelClass = (isBasic) =>
  ['sg-figure-label', dataSourceClass(resolveFinancialSource({ isBasic }))]
    .filter(Boolean)
    .join(' ');

/**
 * Sales (€M) and Employees of one node, rendered like the search list: value plus a 10px ‘yy
 * year, blue for self-disclosed or estimated values (with the search-list tooltip), an info icon
 * for the gross-profit fallback, n/a when unknown and a muted '…' while figures are loading.
 *
 * Default: two label/value columns for the card's bottom row. compact: one inline line,
 * 'Sales (€M) 3.1‘24 · Employees 8‘25', for the compact card and the phone row. knownOnly
 * (compact) leaves out unknown figures, for a line that must also fit '+1 owner above'.
 */
const CardFigures = ({ figures = null, status = 'loaded', compact = false, knownOnly = false }) => {
  const { tooltipId } = useGraphContext();
  const isLoading = !figures && status === 'loading';
  const items = figureItems(figures).filter(
    (item) => !(compact && knownOnly && !isLoading && !item.isKnown)
  );
  if (!items.length) return null;
  const renderValue = ({ Value }) =>
    isLoading ? <LoadingValue /> : <Value figures={figures} tooltipId={tooltipId} />;

  if (compact) {
    return (
      <span className="sg-figures sg-figures--compact">
        {items.map((item, index) => (
          <Fragment key={item.key}>
            {index > 0 && <span className="sg-figures-separator"> · </span>}
            <span className={labelClass(item.isBasic)}>{item.label}</span> {renderValue(item)}
          </Fragment>
        ))}
      </span>
    );
  }

  return (
    <>
      {items.map((item) => (
        <span className="sg-fact sg-figure" key={item.key}>
          <span className={labelClass(item.isBasic)}>{item.label}</span>
          <span className="sg-fact-value">{renderValue(item)}</span>
        </span>
      ))}
    </>
  );
};

const FIGURES_PROP_TYPE = PropTypes.shape({
  revenue: PropTypes.string,
  revenueYear: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isRevenueBasic: PropTypes.bool,
  isRevenueGrossProfit: PropTypes.bool,
  employees: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  employeesYear: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isEmployeesBasic: PropTypes.bool
});

const STATUS_PROP_TYPE = PropTypes.oneOf(['idle', 'loading', 'loaded', 'failed']);

CardFigures.propTypes = {
  figures: FIGURES_PROP_TYPE,
  status: STATUS_PROP_TYPE,
  compact: PropTypes.bool,
  knownOnly: PropTypes.bool
};

/**
 * One figure alone, for a table cell: the same value, year, tooltip, gross-profit icon, n/a and
 * '…' rules as CardFigures.
 */
export const CardFigureValue = ({ figure, figures = null, status = 'loaded' }) => {
  const { tooltipId } = useGraphContext();
  if (!figures && status === 'loading') return <LoadingValue />;
  const Value = figure === 'employees' ? EmployeesValue : RevenueValue;
  return <Value figures={figures} tooltipId={tooltipId} />;
};

CardFigureValue.propTypes = {
  figure: PropTypes.oneOf(['revenue', 'employees']).isRequired,
  figures: FIGURES_PROP_TYPE,
  status: STATUS_PROP_TYPE
};

export default CardFigures;
